import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  type Course,
  getCourseWithProgress,
  getFilteredCourse,
  getStartedCourses
} from '../../api/courcesService';
import StreakBlock from '../components/StreakBlock/StreakBlock';
import './Courses.css';
import CourseCards from './components/CourseCards';
import {CoursesCardsContainer} from "./components/CoursesCardsContainer";
import WideCard from './components/WideCard';
import {smartNavigate} from "../../utils/smartNavigate";



const Courses: React.FC = () => {
  const {t} = useTranslation();
  const [recommendedCourses, setRecommendedCourses] = useState<Course[]>([]);
  const [trendingCourses, setTrendingCourses] = useState<Course[]>([]);
  const [startedCourses, setStartedCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [recommendedLoading, setRecommendedLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {

    function mergeCourses(courses: Course[], withProgress: any) {
      const mergedCourses: Course[] = courses.map((item) => {
        const progressFound = withProgress.find((i: any) => i.id === item.id);
        return {
          ...item,
          ...progressFound,
        };
      });
      return mergedCourses;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setRecommendedLoading(true);

        // Fetch basic courses data
        const coursesData = await getFilteredCourse('recommended'); // recommended | trending | promoted
        const trendingCourses = await getFilteredCourse('trending');
        const stCourses  = await getStartedCourses();

        // const coursesData = await getCourses();

        // Fetch progress data for each course and merge with course data
        const coursesWithProgress = await Promise.all(
          [...coursesData, ...trendingCourses, ...stCourses].map(async (course) => {
            try {
              const courseWithProgress = await getCourseWithProgress(course.id);

              // Calculate progress from the progress data
              let totalLessons = 0;
              let lessonsCompleted = 0;

              if (courseWithProgress.chapters && Array.isArray(courseWithProgress.chapters)) {
                courseWithProgress.chapters.forEach(chapter => {
                  if (chapter.lessons && Array.isArray(chapter.lessons)) {
                    totalLessons += chapter.lessons.length;
                  }
                });
              }

              // Use progressInfo to get finished lessons count
              if (courseWithProgress.progressInfo) {
                lessonsCompleted = courseWithProgress.progressInfo.finishedLessons.length;
              }

              return {
                ...course, // Keep original course data (title, description, etc.)
                ...courseWithProgress, // Override with progress data (chapters with progressInfo)
                totalLessons,
                lessonsCompleted
              };
            } catch (error) {
              console.error(`Error fetching progress for course ${course.id}:`, error);
              // Return original course if progress fetch fails
              return {
                ...course,
                totalLessons: 0,
                lessonsCompleted: 0
              };
            }
          })
        );

        setRecommendedCourses(mergeCourses(coursesData, coursesWithProgress));
        setTrendingCourses(mergeCourses(trendingCourses, coursesWithProgress));
        setStartedCourses(mergeCourses(stCourses, coursesWithProgress));
      } catch (err) {
        setRecommendedCourses([]);
        setError(t('product.courses.error'));
      } finally {
        setLoading(false);
        setRecommendedLoading(false);
      }
    };

    fetchData();
  }, [t]);


  return (
    <div className="courses-container">
      <StreakBlock loading={loading} />

      {/*Your courses Block */}
      <CoursesCardsContainer
        title={t('product.courses.yourCourses')}
        loading={loading}
        linkHref={'/all-courses?filter=started&filterId=all'}
        itemsCount={startedCourses.length}
      >
        <CourseCards
          items={startedCourses}
          loading={loading}
          viewProgress
          onCourseClick={(course) => smartNavigate(`/course/${course.id}`)}
        />
      </CoursesCardsContainer>
      {/* Recommended Courses Section */}
      <CoursesCardsContainer
        title={t('product.courses.recommendedForYou')}
        linkTitle={t('product.courses.seeAll')}
        loading={loading}
        linkHref={'/all-courses?filter=recommended&filterId=all'}
        itemsCount={recommendedCourses.length}
      >
        <CourseCards
          items={recommendedCourses}
          loading={loading}
          onCourseClick={(course) => smartNavigate(`/course/${course.id}`)}
        />
      </CoursesCardsContainer>

      {/* New wide rectangular card block */}
      <WideCard
        course={recommendedCourses[0]}
        loading={recommendedLoading}
        onClick={() => ''}
        // onClick={() => navigate(`/course/${recommendedCourses[0]?.id}`)}
      />
      {/* New and Trending Block */}
      <CoursesCardsContainer
        loading={loading}
        linkHref={'/all-courses?filter=trending&filterId=all'}
        title={t('product.courses.newAndTrending')}
        itemsCount={trendingCourses.length}
      >
        <CourseCards
          items={trendingCourses}
          onCourseClick={(course) => smartNavigate(`/course/${course.id}`)}
        />
      </CoursesCardsContainer>

      {
        error && (
          <div className="courses-error">
            {error}
          </div>
        )
      }

      {
        !loading && !error && [...trendingCourses, ...startedCourses, ...recommendedCourses].length === 0 && (
          <div className="courses-empty">
            {t('product.courses.noCourses')}
          </div>
        )
      }

    </div>
  )
    ;
};

export default Courses;
